import { useState, useEffect } from "react";
import { X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface BannerData {
  id: string;
  title: string;
  message: string;
  cta_text: string;
  cta_url: string;
  discount_type?: string;
  discount_value?: number;
  discount_code?: string;
  background_gradient: string;
  text_color: string;
  show_countdown: boolean;
  countdown_duration: number;
  ends_at?: string;
}

interface LimitedOfferBannerProps {
  onVisibilityChange?: (isVisible: boolean) => void;
}

const LimitedOfferBanner = ({ onVisibilityChange }: LimitedOfferBannerProps) => {
  const [bannerData, setBannerData] = useState<BannerData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActiveBanner();
    
    // Subscribe to real-time banner updates
    const channel = supabase
      .channel('banner-updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'promotional_banners'
      }, () => {
        fetchActiveBanner();
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeLeft]);

  const fetchActiveBanner = async () => {
    try {
      const { data, error } = await supabase
        .from('promotional_banners')
        .select('*')
        .eq('active', true)
        .lte('starts_at', new Date().toISOString())
        .or(`ends_at.is.null,ends_at.gt.${new Date().toISOString()}`)
        .order('priority', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching banner:', error);
        return;
      }
      
      if (data) {
        // Check if user has dismissed this banner
        const dismissed = localStorage.getItem(`banner_dismissed_${data.id}`);
        if (dismissed) {
          return;
        }

        setBannerData(data);
        setIsVisible(true);
        onVisibilityChange?.(true);
        calculateTimeLeft(data);
        trackImpression(data.id);
      }
    } catch (error) {
      console.error('Error in fetchActiveBanner:', error);
    }
  };

  const calculateTimeLeft = (banner: BannerData) => {
    if (banner.show_countdown && banner.ends_at) {
      const diff = Math.floor((new Date(banner.ends_at).getTime() - Date.now()) / 1000);
      setTimeLeft(Math.max(0, diff));
    } else if (banner.show_countdown) {
      setTimeLeft(banner.countdown_duration);
    }
  };

  const trackImpression = async (bannerId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from('banner_interactions').insert({
        banner_id: bannerId,
        user_id: user?.id,
        interaction_type: 'impression'
      });
      
      await supabase.rpc('increment_banner_stat', {
        p_banner_id: bannerId,
        p_stat_name: 'impressions'
      });
    } catch (error) {
      console.error('Error tracking impression:', error);
    }
  };

  const handleClick = async () => {
    if (bannerData) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        await supabase.from('banner_interactions').insert({
          banner_id: bannerData.id,
          user_id: user?.id,
          interaction_type: 'click'
        });
        
        await supabase.rpc('increment_banner_stat', {
          p_banner_id: bannerData.id,
          p_stat_name: 'clicks'
        });
      } catch (error) {
        console.error('Error tracking click:', error);
      }
      
      navigate(bannerData.cta_url);
    }
  };

  const handleDismiss = async () => {
    if (bannerData) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        await supabase.from('banner_interactions').insert({
          banner_id: bannerData.id,
          user_id: user?.id,
          interaction_type: 'dismiss'
        });
        
        await supabase.rpc('increment_banner_stat', {
          p_banner_id: bannerData.id,
          p_stat_name: 'dismissals'
        });
        
        // Store dismissal in localStorage to prevent re-showing
        localStorage.setItem(`banner_dismissed_${bannerData.id}`, 'true');
      } catch (error) {
        console.error('Error tracking dismissal:', error);
      }
    }
    
    setIsVisible(false);
    onVisibilityChange?.(false);
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (!isVisible || !bannerData) return null;

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 bg-gradient-to-r ${bannerData.background_gradient} ${bannerData.text_color} py-3 px-4 transition-all duration-300`}
      style={{ height: '60px' }}
    >
      <div className="container mx-auto flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-1">
          {bannerData.show_countdown && <Clock className="h-5 w-5 animate-pulse" />}
          <div>
            <p className="font-bold">{bannerData.title}</p>
            <p className="text-sm opacity-90">
              {bannerData.message}
              {bannerData.show_countdown && timeLeft > 0 && ` - Ends in ${formatTime(timeLeft)}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleClick}
          >
            {bannerData.cta_text}
          </Button>
          <button
            onClick={handleDismiss}
            className="hover:opacity-75 transition-opacity min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close banner"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LimitedOfferBanner;
